import React, { useRef, useState } from "react";

import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { IconButton, Menu, MenuItem, MenuList } from "@mui/material";
import { VisibilityLock } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { useIsViewerPermission } from "hooks";
import { useUserIdSelector } from "store";
import { FolderKind, IFolder } from "store/types";

import { RoleName } from "../../../constants";
import { useFolderHook } from "../Hooks/use-folder-hook";

const panelMenuPaperSx = {
  borderRadius: "8px",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.12)",
  minWidth: 200,
  padding: "6px 0"
};

const panelMenuItemSx = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "15px",
  color: "#20293C",
  minHeight: 40,
  "&:hover": { backgroundColor: "#ECF1F8" }
};

export type FolderActionsHook = typeof useFolderHook;

export interface Props {
  folder: Pick<IFolder, "id" | "deal_id" | "title" | "is_private" | "parent_id" | "user_id" | "kind">;
  refetchHandler: () => Promise<void>;
  useHook?: FolderActionsHook;
}

export const FolderActions: React.FC<Props> = ({ folder, refetchHandler, useHook = useFolderHook }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const userId = useUserIdSelector();

  const isAnalystDealsViewerPermission = useIsViewerPermission("deals");

  const onMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const onMenuClose = () => setAnchorEl(null);

  const { onRenameFolder, onDeleteFolder, onShareDialog, onSetPrivate, checkOnSetPrivatePermission, onAddTask } =
    useHook({
      folder: {
        id: folder.id,
        title: folder.title,
        is_private: folder.is_private,
        parent_id: folder.parent_id,
        deal_id: folder.deal_id ?? null,
        user_id: folder.user_id,
        kind: folder.kind as FolderKind
      },
      refetchHandler
    });

  const options = [
    {
      title: "Share",
      icon: <PersonAddOutlinedIcon />,
      onClick: () => {
        onShareDialog?.();
      },
      hidden: !onShareDialog || !folder.deal_id || folder.is_private || folder.kind === "dataroom",
      disabled: !onShareDialog
    },
    {
      title: "Rename",
      icon: <EditOutlinedIcon />,
      onClick: () => {
        onRenameFolder?.(menuTriggerRef, onMenuClose);
      },
      hidden: !onRenameFolder || folder.kind === "dataroom",
      disabled: !onRenameFolder
    },
    {
      title: folder.is_private ? "Set to Public" : "Set to Private",
      icon: <VisibilityLock sx={{ "& path": { fill: "#20293D !important" } }} />,
      onClick: () => {
        onSetPrivate?.();
      },
      hidden:
        !folder.deal_id || !checkOnSetPrivatePermission() || folder.kind === "dataroom" || folder.user_id !== userId,
      disabled: !onSetPrivate
    },
    {
      title: "Add Task",
      icon: <CheckOutlinedIcon />,
      onClick: () => {
        onAddTask?.();
      },
      hidden: !folder.deal_id || !onAddTask,
      disabled: !onAddTask
    },
    {
      title: "Delete",
      icon: <DeleteOutlineOutlinedIcon />,
      onClick: () => {
        onDeleteFolder?.();
      },
      hidden: !onDeleteFolder || folder.kind === "dataroom",
      disabled: !onDeleteFolder
    }
  ];

  return (
    <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isAnalystDealsViewerPermission}>
      <IconButton
        size="sm"
        ref={menuTriggerRef}
        className="folder-menu-trigger"
        aria-expanded={Boolean(anchorEl)}
        onClick={onMenuOpen}
        sx={{
          padding: "4px",
          ...(anchorEl && { opacity: "0.7 !important" })
        }}
      >
        <MoreVertIcon sx={{ fontSize: 20, fill: "#0A3142" }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: panelMenuPaperSx } }}
        MenuListProps={{ sx: { padding: 0 } }}
      >
        <MenuList disablePadding>
          {options
            .filter((option) => !option.hidden)
            .map((option) => (
              <MenuItem key={option.title} onClick={option.onClick} disabled={option.disabled} sx={panelMenuItemSx}>
                {option.icon}
                {option.title}
              </MenuItem>
            ))}
        </MenuList>
      </Menu>
    </RoleComponentGuard>
  );
};
